import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  Dimensions,
  TouchableOpacity,
  Platform,
} from 'react-native';
import StatsStyles from './StatsBar.styles';
import { headers } from '../../utils/fetchHeaders';
import config from '../../config';
import { statsBarUpdated } from '../../state/apolloVars';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from '@oolio-group/localization';
import { DocketStatus, KdsTimerMilestone } from '@oolio-group/domain';
import { useOrgTimings } from '../../hooks/useOrgTimings';
import { getStartTimeandEndTime } from '../../utils/timeHelper';
import { useScale } from '../../hooks/useScale';
import { Scale } from '../../common/types';
import Icon from '../Icon/Icon';
import theme from '../../common/theme';
import StatsModal from '../Modals/Stats/StatsModal';
import { useSession } from '../../hooks/useSession';
import { stripProperties } from '@oolio-group/client-utils';

interface StatsProps {
  headers: headers | undefined;
  printerProfileIds: string[];
  storeId: string;
}
export interface StatsInfo {
  avgTime: number;
  longest: number;
  dockets: number;
  items: number;
}
const StatsBar: React.FC<StatsProps> = ({
  headers,
  printerProfileIds,
  storeId,
}) => {
  const styles = StatsStyles();
  const statsBarUpdateInfo = useReactiveVar(statsBarUpdated);
  const { translate } = useTranslation();
  const [stats, setStats] = useState<StatsInfo>({
    avgTime: 0,
    longest: 0,
    dockets: 0,
    items: 0,
  });
  const { dateTime } = useOrgTimings();
  const { currentScale } = useScale();
  const touchable = useRef(null);
  const { session } = useSession();

  const milestones = (session?.kitchenDisplay?.timerMilestones || []).map(
    (x: KdsTimerMilestone) => stripProperties(x, '__typename'),
  ) as KdsTimerMilestone[];

  const getMilestoneColor = (value: number): string => {
    if (!milestones.length) {
      return '#049be5'; // return default color if no milestones
    }

    const applicableMilestone = milestones
      .filter(m => value >= m.minutes)
      .reduce((prev, curr) => (value >= curr.minutes ? curr : prev), {
        label: '',
        minutes: 0,
        color: '#049be5',
      });

    return applicableMilestone.minutes > 0
      ? applicableMilestone.color
      : '#049be5';
  };

  const isOverlappingContent = useMemo(() => {
    const vw = Dimensions.get('window').width;
    const safeWidth = Platform.OS === 'web' ? 1340 : 1440;
    return vw <= safeWidth && currentScale !== Scale.SMALL;
  }, [currentScale]);

  const [showOverFlow, setShowOverFlow] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const timings = getStartTimeandEndTime(dateTime?.startTime || '', true);
        const response = await fetch(
          config.KDS_SERVICE_URI + '/dockets/stats',
          {
            method: 'POST',
            body: JSON.stringify({
              ids: printerProfileIds,
              status: DocketStatus.COMPLETED,
              startTime: timings.startTime,
              endTime: timings.endTime,
              storeId: storeId,
            }),
            headers: headers,
          },
        );
        const result = await response.json();

        setStats(prev => {
          return {
            avgTime: result.AvgTime ?? prev.avgTime,
            longest: result.Longest ?? prev.longest,
            dockets: result.DocketsCount ?? prev.dockets,
            items: result.ItemsCount ?? prev.items,
          };
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [
    printerProfileIds,
    storeId,
    headers,
    statsBarUpdateInfo,
    dateTime?.startTime,
  ]);

  return (
    <View>
      <View style={styles.statsContainer}>
        <View style={styles.statGroup}>
          <Text style={styles.statLabel}>
            {translate('kitchenDisplay.avgTime')}
          </Text>
          <Text
            style={[
              styles.statValue,
              {
                color: getMilestoneColor(stats.avgTime),
              },
            ]}
          >
            {stats.avgTime +
              (stats.avgTime <= 1.0
                ? ` ${translate('kitchenDisplay.minute')}`
                : ` ${translate('kitchenDisplay.minutes')}`)}
          </Text>
        </View>
        <View style={styles.statGroup}>
          <Text style={styles.statLabel}>
            {translate('kitchenDisplay.longestTime')}
          </Text>
          <Text
            style={[
              styles.statValue,
              styles.longest,
              {
                color: getMilestoneColor(stats.longest),
              },
            ]}
          >
            {stats.longest +
              (stats.longest <= 1.0
                ? ` ${translate('kitchenDisplay.minute')}`
                : ` ${translate('kitchenDisplay.minutes')}`)}
          </Text>
        </View>
        {!isOverlappingContent ? (
          <>
            <View style={styles.separator} />
            <View style={styles.statGroup}>
              <Text style={styles.statLabel}>
                {translate('kitchenDisplay.dockets')}
              </Text>
              <Text style={styles.statValue}>{stats.dockets}</Text>
            </View>
            <View style={styles.statGroup}>
              <Text style={styles.statLabel}>
                {translate('kitchenDisplay.items')}
              </Text>
              <Text style={styles.statValue}>{stats.items}</Text>
            </View>
          </>
        ) : (
          <TouchableOpacity
            ref={touchable}
            onPress={() => setShowOverFlow(true)}
            testID="btn-stats-bar-overflow"
          >
            <View>
              <Icon size={20} color={theme.colors.white} name={'ellipsis-h'} />
            </View>
          </TouchableOpacity>
        )}
      </View>
      <StatsModal
        info={stats}
        visible={showOverFlow}
        onClose={() => {
          setShowOverFlow(false);
        }}
      />
    </View>
  );
};

export default StatsBar;
